import { useCallback, useState, type VFC } from 'react';
import { useHistory } from 'react-router-dom';
import { Table } from 'components';
import { RoleName, type SerializedManufacturer } from '@enmon/commons/lib/tools';
import { useDeleteManufacturer } from 'api/hooks/manufacturers/useDeleteManufacturer';
import type { SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { useUserInfo } from '@/hooks/useUser';
import type { Key, SortOrder } from 'antd/lib/table/interface';
import { getManufacturersColumnsConfig } from '../utils/getColumnsConfig';
import { useManufacturersListFilterWithPagination } from '../hooks/useManufacturersListFilterWithPagination.ts';

interface ManufacturersTableProps {
  isFetchingManufacturers: boolean;
  manufacturersData: SerializedManufacturer[];
}

export const ManufacturersTable: VFC<ManufacturersTableProps> = props => {
  const { isFetchingManufacturers, manufacturersData } = props;
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  // State
  const history = useHistory();
  const { pagination, setFilterAndPaginationState } = useManufacturersListFilterWithPagination();
  const [sortState, setSortState] = useState<{ field: Key | null; order: SortOrder }>({
    field: null,
    order: null,
  });

  // Mutations
  const { mutate: deleteManufacturer, isLoading: isDeletingManufacturers } = useDeleteManufacturer();

  // Handlers
  const handleClick = useCallback(
    (manufacturerId: SerializedManufacturer['id']) => {
      history.push(`${history.location.pathname}/${manufacturerId}`);
    },
    [history],
  );

  const handleDelete = useCallback(
    (id: SerializedManufacturer['id']) => {
      deleteManufacturer(id);
    },
    [deleteManufacturer],
  );

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<SerializedManufacturer> | SorterResult<SerializedManufacturer>[],
  ) => {
    const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) ?? {};
    const newFilter = {
      ...(columnKey &&
        order && {
          sort: { [columnKey.toString()]: `${order.slice(0, -3)}` },
        }),
    };
    setSortState({ field: columnKey, order });
    setFilterAndPaginationState(newFilter, newPagination);
  };

  // Table Config
  const isLoading = isFetchingManufacturers || isDeletingManufacturers;

  const tableColumns = getManufacturersColumnsConfig({
    loading: isLoading,
    isMagicRole,
    onClick: handleClick,
    onDelete: handleDelete,
    sortState,
  });

  return (
    <Table<SerializedManufacturer>
      columns={tableColumns}
      dataSource={manufacturersData}
      loading={isLoading}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Manufacturers`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={handleTableChange}
    />
  );
};
